import React from 'react';
import { X, MapPin, Clock, User, FileText, Camera, Mic, CheckCircle, AlertCircle } from 'lucide-react';

export default function VisitDetailsPane({ visit, onClose }) {
  if (!visit) return null;

  return (
    <div className="absolute top-0 right-0 h-full w-[360px] bg-white shadow-2xl z-[1001] flex flex-col border-l">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b bg-gray-50">
        <h3 className="font-semibold text-gray-800 text-sm">Visit Details</h3>
        <button
          onClick={onClose}
          className="p-1.5 hover:bg-gray-200 rounded-lg transition"
        >
          <X size={18} className="text-gray-500" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        {/* Status Badge */}
        <div className="flex items-center gap-2">
          {visit.confirmed ? (
            <span className="flex items-center gap-1.5 bg-green-100 text-green-700 px-3 py-1.5 rounded-full text-sm font-medium">
              <CheckCircle size={14} /> Confirmed Visit
            </span>
          ) : (
            <span className="flex items-center gap-1.5 bg-orange-100 text-orange-700 px-3 py-1.5 rounded-full text-sm font-medium">
              <AlertCircle size={14} /> Unconfirmed Visit
            </span>
          )}
        </div>

        {/* Doctor Info */}
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <User size={18} className="text-primary-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wider">Doctor</p>
              <p className="font-semibold text-gray-800">{visit.doctor_name || 'Not specified'}</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <MapPin size={18} className="text-primary-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wider">Hospital / Clinic</p>
              <p className="font-semibold text-gray-800">{visit.hospital_name || 'Not specified'}</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Clock size={18} className="text-primary-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wider">Time</p>
              <p className="font-semibold text-gray-800">
                {visit.timestamp
                  ? new Date(visit.timestamp).toLocaleString([], {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : 'Unknown'}
              </p>
            </div>
          </div>
        </div>

        {/* GPS Coordinates */}
        {visit.latitude && visit.longitude && (
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">GPS Coordinates</p>
            <p className="text-sm font-mono text-gray-700">
              {visit.latitude.toFixed(6)}, {visit.longitude.toFixed(6)}
            </p>
          </div>
        )}

        {/* Notes */}
        {visit.optional_notes && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <FileText size={16} className="text-primary-600" />
              <p className="text-xs text-gray-400 uppercase tracking-wider">Notes</p>
            </div>
            <div className="bg-blue-50 rounded-lg p-3">
              <p className="text-sm text-gray-700 leading-relaxed">{visit.optional_notes}</p>
            </div>
          </div>
        )}

        {/* Photo */}
        {visit.photo_url && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Camera size={16} className="text-primary-600" />
              <p className="text-xs text-gray-400 uppercase tracking-wider">Photo</p>
            </div>
            <div className="rounded-lg overflow-hidden border">
              <img
                src={visit.photo_url}
                alt="Visit photo"
                className="w-full h-48 object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="hidden items-center justify-center h-48 bg-gray-100 text-gray-400 text-sm">
                Photo unavailable
              </div>
            </div>
          </div>
        )}

        {/* Voice Recording */}
        {visit.voice_url && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Mic size={16} className="text-primary-600" />
              <p className="text-xs text-gray-400 uppercase tracking-wider">Voice Note</p>
            </div>
            <audio controls className="w-full" src={visit.voice_url}>
              Your browser does not support the audio element.
            </audio>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="border-t px-5 py-3 bg-gray-50">
        <p className="text-xs text-gray-400">
          Visit ID: {visit.id ? visit.id.substring(0, 8) + '...' : 'N/A'}
        </p>
      </div>
    </div>
  );
}
